import {
  Button,
  LabeledTextarea,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useAlert,
  useToast,
} from "@apex/component-library";
import {
  MenuItem,
  MenuItemProps,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { withFormikDevtools } from "formik-devtools-extension";

import { usePostCallAddNote } from "../../../api/usePostCallAddNote";
import { SUCCESS_TOAST } from "../../../utils/constants";
import AddNoteSchema, { MAX_NOTE_CHAR } from "./AddNoteSchema";

export interface AddNoteProps extends MenuItemProps {
  eventId: string;
  account: string;
  onSuccess?: () => void;
}

export interface AddNoteFormValues {
  note: string;
}

const AddNote = ({ eventId, onSuccess }: AddNoteProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { removeAllAlerts } = useAlert();

  const handleClose = () => {
    onClose();
  };
  const { mutate, isLoading } = usePostCallAddNote(eventId, {
    onSuccess: () => {
      // show toast and close
      toast({
        ...SUCCESS_TOAST,
        description: "Your note has been added.",
      });

      removeAllAlerts();
      !!onSuccess && onSuccess();
    },
    onSettled: () => {
      handleClose();
    },
  });

  const formikConfig = {
    onSubmit: (values: AddNoteFormValues) => mutate(values.note),
    initialValues: AddNoteSchema.cast({}) as AddNoteFormValues,
    validationSchema: AddNoteSchema,
    validateOnMount: true,
  };
  return (
    <>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={!isLoading}
      >
        <ModalOverlay />
        <ModalContent>
          <Formik {...formikConfig}>
            {formik => {
              withFormikDevtools(formik);
              return (
                <Form>
                  <ModalHeader>Add Note</ModalHeader>
                  <ModalCloseButton isDisabled={isLoading} />
                  <ModalBody>
                    <LabeledTextarea
                      label="Note"
                      name="note"
                      isRequired
                      charCounter={MAX_NOTE_CHAR}
                      value={formik.values.note}
                      onChange={e => {
                        formik.setFieldValue(
                          "note",
                          e.target.value.substring(0, MAX_NOTE_CHAR)
                        );
                      }}
                      errorMessage={formik.errors.note}
                      isInvalid={formik.touched.note && !!formik.errors.note}
                      onBlur={formik.handleBlur}
                      isDisabled={isLoading}
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outline"
                        onClick={handleClose}
                        isDisabled={isLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isDisabled={!formik.isValid}
                        isLoading={isLoading}
                      >
                        Confirm
                      </Button>
                    </Stack>
                  </ModalFooter>
                </Form>
              );
            }}
          </Formik>
        </ModalContent>
      </Modal>
      <MenuItem
        data-testid="action-add-note"
        onClick={e => {
          e.stopPropagation();
          onOpen();
        }}
      >
        Add Note
      </MenuItem>
    </>
  );
};

export default AddNote;
